import React from "react";
import { GlassCard } from "../components/GlassCard";
import { useBudget } from "../context/BudgetContext";
import { useI18n } from "../context/I18nContext";
import { formatCurrency, formatPercent } from "../lib/i18n";
import { CONSTANTS } from "../lib/constants";
import { RotateCcw, TrendingUp, PiggyBank } from "lucide-react";

const Slider = ({ label, value, onChange, max, testId }) => (
  <div className="flex flex-col gap-3" data-testid={testId}>
    <div className="flex items-baseline justify-between">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <span className="text-2xl font-medium text-indigo-950 tabular-nums" style={{ fontFamily: "Outfit, sans-serif" }}>
        {(value * 100).toFixed(0)}%
      </span>
    </div>
    <input
      data-testid={`${testId}-range`}
      type="range"
      min={0}
      max={max * 100}
      step={1}
      value={value * 100}
      onChange={(e) => onChange(Number(e.target.value) / 100)}
      className="w-full h-2 accent-indigo-950 rounded-full"
    />
    <div className="flex justify-between text-xs text-slate-400">
      <span>0%</span>
      <span>{max * 100}%</span>
    </div>
  </div>
);

export const OptimisationModule = () => {
  const { t, lang } = useI18n();
  const { state, setOptimField, indicators, optim } = useBudget();
  const { tauxVariables, tauxFixes } = state.optimisation;

  const handleReset = () => {
    setOptimField("tauxVariables", CONSTANTS.TAUX_OPTIM_VARIABLES_DEFAUT);
    setOptimField("tauxFixes", CONSTANTS.TAUX_OPTIM_FIXES_DEFAUT);
  };

  return (
    <div className="flex flex-col gap-8" data-testid="module-optimisation">
      <header className="flex flex-col gap-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
          {t.modules.optimisation}
        </span>
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          {t.optimisation.title}
        </h1>
        <p className="text-slate-500 text-base max-w-2xl">{t.optimisation.subtitle}</p>
      </header>

      <GlassCard>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t.optimisation.title}
          </h2>
          <button
            data-testid="btn-optim-reset"
            onClick={handleReset}
            className="flex items-center gap-2 bg-white/80 text-indigo-950 border border-slate-200 hover:bg-slate-50 rounded-xl px-4 py-2 text-sm font-medium transition-all"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {t.optimisation.reset}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Slider
            testId="slider-variables"
            label={t.optimisation.tauxVariables}
            value={tauxVariables}
            max={0.5}
            onChange={(v) => setOptimField("tauxVariables", v)}
          />
          <Slider
            testId="slider-fixes"
            label={t.optimisation.tauxFixes}
            value={tauxFixes}
            max={0.3}
            onChange={(v) => setOptimField("tauxFixes", v)}
          />
        </div>
      </GlassCard>

      {/* Results */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <GlassCard data-testid="optim-economies-variables">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.optimisation.economiesVariables}</span>
          <p className="text-3xl font-medium text-indigo-950 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {formatCurrency(optim.economiesVariables, lang)}
          </p>
        </GlassCard>
        <GlassCard data-testid="optim-economies-fixes">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.optimisation.economiesFixes}</span>
          <p className="text-3xl font-medium text-indigo-950 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {formatCurrency(optim.economiesFixes, lang)}
          </p>
        </GlassCard>
      </div>

      {/* Highlight */}
      <div
        data-testid="optim-gain-mensuel"
        className="rounded-2xl bg-gradient-to-br from-indigo-950 to-indigo-800 text-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xl"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-emerald-400/20 flex items-center justify-center">
            <PiggyBank className="w-6 h-6 text-emerald-300" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60">{t.optimisation.gainMensuel}</div>
            <div className="text-sm text-white/70 mt-1">{t.revenus.perMonth}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-5xl font-medium tracking-tighter text-emerald-300" style={{ fontFamily: "Outfit, sans-serif" }}>
            {formatCurrency(optim.gainMensuel, lang)}
          </div>
          <div className="text-sm text-white/70 mt-2">
            {t.optimisation.epargneAnnuelle}: <span className="text-emerald-300 font-medium">{formatCurrency(optim.epargneAnnuelle, lang)}</span>
          </div>
        </div>
      </div>

      {/* Before / after */}
      <GlassCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-indigo-950" />
          </div>
          <h2 className="text-lg font-medium text-indigo-950" style={{ fontFamily: "Outfit, sans-serif" }}>
            {t.optimisation.before} / {t.optimisation.after}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-5" data-testid="optim-rav-avant">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.optimisation.before} — {t.analyse.resteAVivre}</span>
            <p className="text-2xl font-medium text-slate-700 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatCurrency(indicators.resteAVivre, lang)}
            </p>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5" data-testid="optim-rav-apres">
            <span className="text-xs uppercase tracking-[0.18em] text-emerald-700">{t.optimisation.after} — {t.optimisation.nouveauRav}</span>
            <p className="text-2xl font-medium text-emerald-700 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatCurrency(optim.nouveauRav, lang)}
            </p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-5" data-testid="optim-taux-avant">
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{t.optimisation.before} — {t.analyse.tauxEndettement}</span>
            <p className="text-2xl font-medium text-slate-700 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatPercent(indicators.tauxEndettement, lang)}
            </p>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5" data-testid="optim-taux-apres">
            <span className="text-xs uppercase tracking-[0.18em] text-emerald-700">{t.optimisation.after} — {t.optimisation.nouveauTaux}</span>
            <p className="text-2xl font-medium text-emerald-700 mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {formatPercent(optim.nouveauTaux, lang)}
            </p>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};
